import { InputAdornment, TextField } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import styled from "@emotion/styled";
import dayjs from "dayjs";
import { useEffect, useState, useRef } from "react";
import { NO_CONTACT_PLACEHOLDER } from "./transaction_fields";

// Re-exported so existing `general_util` importers get the contact helpers from
// the module they already use. New code should import from `transaction_fields`.
export {
  NO_CONTACT_PLACEHOLDER,
  isBlankContact,
  isJournalEntryTransaction,
} from "./transaction_fields";

// Display values that carry no digits — a currency input holding one of these
// on blur clears to "" rather than being formatted to cents.
const BLANK_CURRENCY_DISPLAYS = ["", "-", ".", "-.", "-.0"];

// Default fallback currency for callers that haven't been org-aware-ified yet.
// Pass a `currencyCode` (ISO 4217) to format in any currency. Most callers
// inside UserContext should pass `org.homeCurrency` (Phase 5 migration).
export const returnCurrency = (cents, currencyCode = "USD") => {
  if (isNaN(cents)) {
    return "Invalid number";
  }

  const dollars = cents / 100;

  let formatted;
  try {
    formatted = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode || "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(dollars);
  } catch {
    // Unknown ISO code — fall back to USD formatting
    formatted = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(dollars);
  }

  return formatted;
};

export const formatNumberWithCommas = (value) => {
  // Ensure that we're formatting only if there's a value
  if (!value) return "";
  let parts = value.split(".");
  let integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  // Leading-decimal input (".5", "-.5") has no integer digits — emit the zero
  // so blur formats to "0.50" rather than ".50"
  if (parts.length === 2 && (integerPart === "" || integerPart === "-")) {
    integerPart += "0";
  }
  // Reassemble the parts to include the decimal, if present
  return parts.length === 2 ? `${integerPart}.${parts[1]}` : integerPart;
};

export const removeCommasAndReturnCents = (value) => {
  if (value === "-" || value === 0 || value === "0") {
    value = 0;
    return value;
  } else {
    return Math.round(parseFloat(value.toString().replace(/,/g, "")) * 100);
  }
};

export const returnCurrencyInput = (input) => {
  // Guard: Handle null/undefined/empty at the top to prevent crashes
  if (input === null || input === undefined || input === "") {
    return { formattedDollarValue: "", centsInt: 0 };
  }

  // Convert input to string if it's not already
  let inputString = typeof input === "string" ? input : input.toString();
  let value = inputString.replace(/,/g, "");

  const dotIndex = value.indexOf(".");
  if (dotIndex !== -1 && value.length - dotIndex - 1 > 2) {
    value = value.substring(0, dotIndex + 3);
  }

  // A bare "." is a valid intermediate keystroke on the way to ".62" — pass it
  // through so the controlled input keeps the character. No digits yet, so the
  // stored value must stay 0 cents.
  if (value === ".") {
    return { formattedDollarValue: ".", centsInt: 0 };
  }

  const isNumberOrDecimal = /^-?(\d+\.?\d{0,2}|\.\d{1,2})$/.test(value);

  if (isNumberOrDecimal) {
    const numericValue = parseFloat(value);

    // Prevent NaN from ever being returned (would corrupt Firestore data)
    if (isNaN(numericValue)) {
      return { formattedDollarValue: "", centsInt: 0 };
    }

    const formattedValue = formatNumberWithCommas(value);
    const centsInt = Math.round(numericValue * 100);

    // Normalize -0 to 0 to prevent edge case issues
    return {
      formattedDollarValue: formattedValue,
      centsInt: Object.is(centsInt, -0) ? 0 : centsInt,
    };
  }

  // Invalid input - return safe defaults
  return {
    formattedDollarValue: "",
    centsInt: 0,
  };
};

export const formatCurrency = (valueIn) => {
  if (!isNaN(valueIn)) {
    const dollars = parseFloat(valueIn / 100).toFixed(2);
    return formatNumberWithCommas(dollars);
  } else if (
    valueIn !== undefined &&
    valueIn !== null &&
    valueIn !== "" &&
    isNaN(valueIn)
  ) {
    const numericAmount = parseFloat(valueIn.replace(/,/g, "")).toFixed(2);
    const formattedAmount = formatNumberWithCommas(numericAmount);
    return formattedAmount;
  }
  return valueIn;
};

//Function to find and link an account given an account number and account Data
//If no match can be found, returns null
export const findAccountById = ({ accountId, accounts }) => {
  const account = accounts.find((account) => {
    return account.id === accountId;
  });
  return account;
};

// The one definition of a fund that fund pickers offer and that counts
// toward single-fund-org detection (context_and_index.jsx). Keeping both
// behind one predicate guarantees "one fund in dropdowns" and "single-fund
// mode" can never disagree.
export const isUsableFund = (fund) =>
  !fund.disabled &&
  Boolean(fund.fundName?.trim()) &&
  Boolean(fund.fundNumber?.toString().trim());

//Function to find and link a fund given a fund number and fund Data
//If no match can be found, returns null
export const findFundById = ({ fundId, funds }) => {
  // If fundId is null or undefined, return a safe default object
  if (!fundId) {
    return { fundName: "Unknown Fund", fundNumber: "" };
  }

  // If funds array is empty or undefined, return a safe default object
  if (!funds || funds.length === 0) {
    return { fundName: "Unknown Fund", fundNumber: "" };
  }

  const fund = funds.find((fund) => {
    return fund && fund.id === fundId;
  });

  if (!fund) {
    console.warn(`No fund found for fundId: ${fundId}`);
    return { fundName: `Fund ${fundId}`, fundNumber: "" };
  }

  return fund;
};

//Function to find and link a label given a label ID and label Data
//If no match can be found, returns null
export const findLabelById = ({ labelId, labels }) => {
  // If labelId is null or undefined, return null (labels are optional)
  if (!labelId) {
    return null;
  }

  // If labels array is empty or undefined, return a safe default object
  if (!labels || labels.length === 0) {
    return { labelName: "Unknown Label", labelNumber: "" };
  }

  const label = labels.find((label) => {
    return label && label.id === labelId;
  });

  if (!label) {
    console.warn(`No label found for labelId: ${labelId}`);
    return { labelName: `Label ${labelId}`, labelNumber: "" };
  }

  return label;
};

export const findContactNameById = ({ contactId, contacts, donors }) => {
  // If contactId is null or undefined, return default value
  if (!contactId) {
    return NO_CONTACT_PLACEHOLDER;
  }

  // If contacts array is empty or undefined, return a safe default
  if (!contacts || contacts.length === 0) {
    // Still check donors before giving up
    if (donors && donors.length > 0) {
      const donor = donors.find(
        (d) => d && Array.isArray(d.id) && d.id.some((id) => id === contactId),
      );
      if (donor) return donor.shortName || "Unnamed Donor";
    }
    return "Unknown Contact";
  }

  const contact = contacts.find((contact) => {
    return contact && contact.id === contactId;
  });

  if (!contact) {
    // Fall back to donors if available
    if (donors && donors.length > 0) {
      const donor = donors.find(
        (d) => d && Array.isArray(d.id) && d.id.some((id) => id === contactId),
      );
      if (donor) return donor.shortName || "Unnamed Donor";
    }
    console.warn(`No contact found for contactId: ${contactId}`);
    return "Unknown Contact";
  }

  return contact.shortName || "Unnamed Contact";
};

export const findDonorName = ({ donorId, donors }) => {
  // Normalize donorId — may be a string or an array (donations store arrays)
  const idsToMatch = Array.isArray(donorId) ? donorId : [donorId];
  const donor = donors.find((d) => {
    return d.id.some((idItem) => idsToMatch.includes(idItem));
  });
  if (!donor) {
    console.error("No donor found for donorId: ", donorId);
    return "No Donor Found";
  }
  return donor.shortName;
};

/**
 * Every id a donation may carry for this donor: the donor doc id
 * (`firestoreId`) plus each merge-alias id from `id` (pre-merge donations keep
 * the alias they were written with). Deduped, order-stable, falsy dropped.
 *
 * @param {Object} donor - donor record with `firestoreId` and `id` (string or string[])
 * @return {string[]} donor ids to match donations against
 */
export const donorAliasIds = (donor) => {
  const ids = [];
  const seen = new Set();
  const candidates = [
    donor?.firestoreId,
    ...(Array.isArray(donor?.id) ? donor.id : [donor?.id]),
  ];
  for (const id of candidates) {
    if (id && !seen.has(id)) {
      seen.add(id);
      ids.push(id);
    }
  }
  return ids;
};

export const convertToDate = (date) => {
  // Handle null, undefined, or empty values gracefully
  if (date === null || date === undefined || date === "") {
    console.warn(
      "convertToDate received null/undefined/empty date, returning current date",
    );
    return dayjs(); // Return current date as fallback
  }

  // Handle already valid dayjs objects
  if (dayjs.isDayjs(date)) {
    return date;
  }

  // Handle string dates
  if (typeof date === "string") {
    const parsedDate = dayjs(date);
    if (!parsedDate.isValid()) {
      console.warn(
        `convertToDate received invalid date string: "${date}", returning current date`,
      );
      return dayjs(); // Return current date as fallback
    }
    return parsedDate;
  }

  // Handle JavaScript Date objects
  if (date instanceof Date) {
    const dayjsDate = dayjs(date);
    if (!dayjsDate.isValid()) {
      console.warn(
        `convertToDate received invalid Date object: ${date}, returning current date`,
      );
      return dayjs(); // Return current date as fallback
    }
    return dayjsDate;
  }

  // Handle Firebase timestamp objects (with seconds property)
  if (date && typeof date.seconds === "number") {
    const dayjsDate = dayjs(date.seconds * 1000);
    if (!dayjsDate.isValid()) {
      console.warn(
        `convertToDate received invalid Firebase timestamp: ${JSON.stringify(date)}, returning current date`,
      );
      return dayjs(); // Return current date as fallback
    }
    return dayjsDate;
  }

  // Handle dayjs-like objects with $D, $y properties
  if (date && date.$D && date.$y) {
    try {
      const dayjsDate = dayjs(
        new Date(
          date.$y,
          date.$M,
          date.$D,
          date.$H || 0,
          date.$m || 0,
          date.$s || 0,
          date.$ms || 0,
        ),
      );
      if (!dayjsDate.isValid()) {
        console.warn(
          `convertToDate received invalid dayjs-like object: ${JSON.stringify(date)}, returning current date`,
        );
        return dayjs(); // Return current date as fallback
      }
      return dayjsDate;
    } catch (error) {
      console.warn(
        `convertToDate failed to parse dayjs-like object: ${JSON.stringify(date)}, error: ${error.message}, returning current date`,
      );
      return dayjs(); // Return current date as fallback
    }
  }

  // Handle numeric timestamps (milliseconds)
  if (typeof date === "number" && !isNaN(date)) {
    const dayjsDate = dayjs(date);
    if (!dayjsDate.isValid()) {
      console.warn(
        `convertToDate received invalid numeric timestamp: ${date}, returning current date`,
      );
      return dayjs(); // Return current date as fallback
    }
    return dayjsDate;
  }

  // Final fallback - log the unsupported type but don't crash
  console.warn(
    `convertToDate received unsupported date type: ${typeof date}, value: ${JSON.stringify(date)}, returning current date`,
  );
  return dayjs(); // Return current date as fallback instead of throwing
};

export const generateRandomId = () => {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let autoId = "";
  for (let i = 0; i < 20; i++) {
    autoId += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return autoId;
};

export const amountToCentsInt = (amount) => {
  if (isNaN(parseFloat(amount))) {
    return null;
  } else {
    return Math.round(parseFloat(amount) * 100);
  }
};

export const centsToDollarString = (cents) => {
  if (isNaN(cents)) {
    return null;
  } else {
    // Divide by 100 to convert cents to dollars and format to two decimal places
    return (cents / 100).toFixed(2);
  }
};

export const sortAccountsByType = (
  accounts,
  accountTypeOrder,
  selectedAccount = "",
  disabledAccountType = "",
) => {
  //Check if there are accounts, return an empty array if there are none
  if (!accounts) {
    return [];
  }
  return accounts
    .filter(
      (account) =>
        account.id !== selectedAccount &&
        account.accountType.toLowerCase() !==
          disabledAccountType.toLowerCase() &&
        !account.disabled,
    )
    .sort((a, b) => {
      // Get the order index for each account type
      const orderA = accountTypeOrder.indexOf(a.accountType.toLowerCase());
      const orderB = accountTypeOrder.indexOf(b.accountType.toLowerCase());

      // First, sort by the predefined account type order
      if (orderA !== orderB) {
        return orderA - orderB;
      }

      // Then, sort by account number within each type
      return a.accountNumber.toString().localeCompare(b.accountNumber);
    });
};

// // Usage
// const accountTypeOrder = ["expenses", "income", "assets", "liabilities"];
// const disabledAccountType = "Equity"; // Can be any account type or left blank
// const sortedAccounts = sortAccountsByType(accounts, accountTypeOrder, disabledAccountType);

// options = {
//   accounts?.length > 0 ? sortedAccounts : []
// };

export function removeDuplicatesByField(array, fieldName) {
  const seen = new Set();
  return array.filter((item) => {
    const value = item[fieldName];
    if (seen.has(value)) {
      return false;
    } else {
      seen.add(value);
      return true;
    }
  });
}

export const unixDateToString = (unixTimestamp) => {
  const date = new Date(unixTimestamp * 1000); // Ensure multiplication by 1000 for milliseconds
  const month = (date.getMonth() + 1).toString().padStart(2, "0"); // Months are 0-indexed
  const day = date.getDate().toString().padStart(2, "0");
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

export const firebaseTimestampToString = (firebaseTimestamp) => {
  // Convert Firebase Timestamp to JavaScript Date object
  const date = firebaseTimestamp.toDate(); // Assuming firebaseTimestamp is a Firebase Timestamp object

  // Format the date
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const year = date.getFullYear();

  return `${month}/${day}/${year}`;
};

export const generateResponsiveStyles = (breakpoints) => {
  return breakpoints
    .map((breakpoint) => {
      const mediaQuery = `@media (max-width: ${breakpoint.size}px) {`;
      const styles = Object.entries(breakpoint.styles)
        .map(([key, value]) => `${key}: ${value};`)
        .join(" ");
      return `${mediaQuery} ${styles} }`;
    })
    .join("");
};

export const shortenText = (text) => {
  //If the name is more than than 30 characters, shorten it by showing the first letter of each word if there are three or more words, otherwise only show the first word that is longer than three letters.
  if (text.length > 15) {
    const words = text.split(" ");
    if (words.length > 2) {
      return words
        .map((word) => {
          return word[0];
        })
        .join("");
    } else {
      return words.find((word) => word.length > 3);
    }
  }
  return text;
};

export const extremeShortenText = (text) => {
  if (text.length > 7) {
    const words = text.split(" ");
    if (words.length > 3) {
      return words
        .map((word) => {
          return word[0];
        })
        .join("");
    } else {
      return text.substring(0, 6) + "...";
    }
  } else {
    return text;
  }
};

// // Usage for text component
// const textStyle = css`
//   ${generateResponsiveStyles([
//     { size: 2200, styles: { "font-size": "1.4rem" } },
//     { size: 1200, styles: { "font-size": "1.2rem" } },
//     // Add more breakpoints as needed
//   ])}
// `;

// // Usage for another component that requires width adjustments
// const boxStyle = css`
//   ${generateResponsiveStyles([
//     { size: 2200, styles: { width: "50%", "font-size": "1.4rem" } },
//     { size: 1200, styles: { width: "70%", "font-size": "1.2rem" } },
//     // Add more breakpoints and styles as needed
//   ])}
// `;

export const CurrencyInput = ({
  currencyValueCents,
  setCurrencyValueCents,
  label,
  size,
  onBlur,
  helperText,
  disabled,
  onKeyDown,
  placeholder,
  inputRef,
  ...otherProps // data-testid / aria-* land on the TextField root, as in ThemedCurrencyInput
}) => {
  const theme = useTheme();
  const [currencyDisplayValue, setCurrencyDisplayValue] = useState(() => {
    if (currencyValueCents === "") return "";
    const initialDollarValue = centsToDollarString(currencyValueCents);
    const { formattedDollarValue } = returnCurrencyInput(initialDollarValue);
    return formattedDollarValue || "";
  });

  // Use ref to track if input is currently focused to avoid interference with typing
  const isInputFocused = useRef(false);

  const handleChangeAmount = (event) => {
    let value = event.target.value;
    // Check if the value is just "-"
    if (value === "-" || value === "-." || value === "-.0") {
      setCurrencyDisplayValue(value);
      return; // Exit early to avoid further processing
    }

    let cleanValue = value.replace(/[,\\]/g, "");
    // Count "-" occurrences
    const dashCount = (cleanValue.match(/-/g) || []).length;

    // Toggle the negative sign based on the count of "-"
    if (dashCount % 2 === 1) {
      // Ensure "-" is at the beginning if the count is odd
      cleanValue = "-" + cleanValue.replace(/-/g, "");
    } else {
      // Remove all "-" if the count is even
      cleanValue = cleanValue.replace(/-/g, "");
    }

    // Validate the resulting value
    const isNumberOrDecimal = /^-?\d*\.?\d*$/; // Allow numbers with or without leading "-", and with or without decimals
    if (cleanValue === "" || isNumberOrDecimal.test(cleanValue)) {
      const { centsInt, formattedDollarValue } =
        returnCurrencyInput(cleanValue);

      setCurrencyValueCents(centsInt);
      setCurrencyDisplayValue(formattedDollarValue);
    } else {
      // Optionally handle invalid input, such as by resetting it to the last valid state or providing feedback
    }
  };

  //useEffect to handle when currencyValueCents changes externally
  useEffect(() => {
    if (currencyValueCents === "") {
      setCurrencyDisplayValue("");
    } else if (
      (!isInputFocused.current || currencyDisplayValue === "") && // Also sync when display is empty (e.g. ghost amount accept)
      currencyValueCents !== "" &&
      currencyValueCents !== null &&
      currencyValueCents !== undefined
    ) {
      // Check if current display value matches the cents value to avoid unnecessary updates
      const currentDisplayAsCents = currencyDisplayValue
        ? returnCurrencyInput(currencyDisplayValue.replace(/,/g, "")).centsInt
        : 0;

      if (currentDisplayAsCents !== currencyValueCents) {
        const dollarValue = centsToDollarString(currencyValueCents);
        if (dollarValue) {
          const { formattedDollarValue } = returnCurrencyInput(dollarValue);
          setCurrencyDisplayValue(formattedDollarValue || "");
        }
      }
    }
  }, [currencyValueCents, currencyDisplayValue]);

  const handleFocus = () => {
    isInputFocused.current = true;
  };

  const handleBlur = () => {
    isInputFocused.current = false;

    if (
      currencyValueCents === "" ||
      BLANK_CURRENCY_DISPLAYS.includes(currencyDisplayValue)
    ) {
      setCurrencyDisplayValue("");
    } else {
      setCurrencyDisplayValue(addCentsOnBlur(currencyDisplayValue));
    }
    if (onBlur) {
      onBlur();
    }
  };

  const addCentsOnBlur = (value) => {
    if (value.includes(".")) {
      let parts = value.split(".");
      if (parts[1].length === 1) {
        // If there is only one digit after the decimal
        return value + "0";
      } else if (parts[1].length === 2) {
        // If there are already two digits after the decimal
        return value;
      } else {
        // If there are more than two digits after the decimal, truncate to two digits
        return parts[0] + "." + parts[1].substring(0, 2);
      }
    } else {
      // If there is no decimal point
      return value + ".00";
    }
  };

  return (
    <TextField
      id={label ? `balance-${label.toLowerCase().replace(/\s+/g, '-')}` : "balance"}
      name={`balance`}
      label={label ? label : ""}
      color={theme.palette.mode === "dark" ? "primary" : "secondary"}
      value={currencyDisplayValue}
      onChange={handleChangeAmount}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={onKeyDown}
      disabled={disabled}
      placeholder={placeholder}
      inputRef={inputRef}
      InputProps={
        currencyValueCents !== null &&
        currencyValueCents !== undefined &&
        currencyValueCents !== ""
          ? {
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            }
          : undefined
      }
      variant="outlined"
      size={size ? size : "medium"}
      helperText={helperText ? helperText : ""}
      {...otherProps}
    />
  );
};

export const ThemedCurrencyInput = ({
  currencyValueCents,
  setCurrencyValueCents,
  label,
  size,
  onBlur,
  helperText,
  disabled,
  startIcon, // Optional icon component (e.g., AccountBalance)
  enhanced = false, // Whether to apply enhanced styling
  renderSmall = false, // New prop for smaller rendering
  sx, // Additional custom styles
  ...otherProps
}) => {
  const theme = useTheme();
  const [currencyDisplayValue, setCurrencyDisplayValue] = useState(() => {
    if (currencyValueCents === "") {
      return "";
    } else {
      const initialDollarValue = centsToDollarString(currencyValueCents);
      const { formattedDollarValue } = returnCurrencyInput(initialDollarValue);
      return formattedDollarValue || "";
    }
  });

  // Use ref to track if input is currently focused to avoid interference with typing
  const isInputFocused = useRef(false);

  const handleChangeAmount = (event) => {
    let value = event.target.value;
    // Check if the value is just "-"
    if (value === "-" || value === "-." || value === "-.0") {
      setCurrencyDisplayValue(value);
      return; // Exit early to avoid further processing
    }

    let cleanValue = value.replace(/[,\\]/g, "");
    // Count "-" occurrences
    const dashCount = (cleanValue.match(/-/g) || []).length;

    // Toggle the negative sign based on the count of "-"
    if (dashCount % 2 === 1) {
      // Ensure "-" is at the beginning if the count is odd
      cleanValue = "-" + cleanValue.replace(/-/g, "");
    } else {
      // Remove all "-" if the count is even
      cleanValue = cleanValue.replace(/-/g, "");
    }

    // Validate the resulting value
    const isNumberOrDecimal = /^-?\d*\.?\d*$/; // Allow numbers with or without leading "-", and with or without decimals
    if (cleanValue === "" || isNumberOrDecimal.test(cleanValue)) {
      // Format the display value, including any decimals accurately
      const { centsInt, formattedDollarValue } =
        returnCurrencyInput(cleanValue);

      setCurrencyValueCents(centsInt);
      setCurrencyDisplayValue(formattedDollarValue);
    }
  };

  //useEffect to handle when currencyValueCents changes externally
  useEffect(() => {
    if (currencyValueCents === "") {
      setCurrencyDisplayValue("");
    } else if (
      (!isInputFocused.current || currencyDisplayValue === "") && // Also sync when display is empty (e.g. ghost amount accept)
      currencyValueCents !== "" &&
      currencyValueCents !== null &&
      currencyValueCents !== undefined
    ) {
      // Check if current display value matches the cents value to avoid unnecessary updates
      const currentDisplayAsCents = currencyDisplayValue
        ? returnCurrencyInput(currencyDisplayValue.replace(/,/g, "")).centsInt
        : 0;

      if (currentDisplayAsCents !== currencyValueCents) {
        const dollarValue = centsToDollarString(currencyValueCents);
        if (dollarValue) {
          const { formattedDollarValue } = returnCurrencyInput(dollarValue);
          setCurrencyDisplayValue(formattedDollarValue || "");
        }
      }
    }
  }, [currencyValueCents, currencyDisplayValue]);

  const handleFocus = () => {
    isInputFocused.current = true;
  };

  const handleBlur = () => {
    isInputFocused.current = false;

    if (
      currencyValueCents === "" ||
      BLANK_CURRENCY_DISPLAYS.includes(currencyDisplayValue)
    ) {
      setCurrencyDisplayValue("");
    } else {
      setCurrencyDisplayValue(addCentsOnBlur(currencyDisplayValue));
    }
    if (onBlur) {
      onBlur();
    }
  };

  const addCentsOnBlur = (value) => {
    if (value.includes(".")) {
      let parts = value.split(".");
      if (parts[1].length === 1) {
        // If there is only one digit after the decimal
        return value + "0";
      } else if (parts[1].length === 2) {
        // If there are already two digits after the decimal
        return value;
      } else {
        // If there are more than two digits after the decimal, truncate to two digits
        return parts[0] + "." + parts[1].substring(0, 2);
      }
    } else {
      // If there is no decimal point
      return value + ".00";
    }
  };

  // Enhanced styling for reconciliation-style inputs
  const enhancedSx = enhanced
    ? {
        "& .MuiOutlinedInput-root": {
          fontFamily: "MontserratReg",
          fontSize: "1.1rem",
          ...sx?.["& .MuiOutlinedInput-root"],
        },
        ...sx,
      }
    : renderSmall
      ? {
          "& .MuiOutlinedInput-root": {
            fontSize: "0.875rem",
            padding: "0px",
            "& input": {
              padding: "6px 8px",
            },
          },
          "& .MuiInputLabel-root": {
            fontSize: "0.875rem",
          },
          ...sx,
        }
      : sx;

  // Build InputProps with optional icon - only show $ if there's a value
  const inputProps =
    currencyDisplayValue !== ""
      ? {
          startAdornment: (
            <InputAdornment
              position="start"
              sx={
                renderSmall ? { "& p": { fontSize: "0.875rem" } } : undefined
              }>
              {startIcon && startIcon}
              <span style={{ marginLeft: startIcon ? 8 : 0 }}>$</span>
            </InputAdornment>
          ),
        }
      : {};

  return (
    <TextField
      id={label ? `balance-${label.toLowerCase().replace(/\s+/g, '-')}` : "balance"}
      name="balance"
      label={label || ""}
      color={theme.palette.mode === "dark" ? "primary" : "secondary"}
      value={currencyDisplayValue}
      onChange={handleChangeAmount}
      onFocus={handleFocus}
      onBlur={handleBlur}
      disabled={disabled}
      InputProps={inputProps}
      variant={renderSmall ? "standard" : "outlined"}
      size={size || "medium"}
      helperText={helperText || ""}
      sx={enhancedSx}
      {...otherProps}
    />
  );
};

export const deepClone = (obj) => {
  // Check if 'structuredClone' is available in the current environment
  if (typeof structuredClone === "function") {
    return structuredClone(obj);
  } else {
    // Fallback to JSON.stringify/JSON.parse for deep cloning
    return JSON.parse(JSON.stringify(obj));
  }
};
